/**
 * Fusion — engagement components
 *
 * Countdown timer, click-to-load video, gallery lightbox and the testimonial
 * carousel. Powers sections/countdown-timer.liquid, sections/video.liquid,
 * sections/image-gallery.liquid and sections/testimonials.liquid.
 */

(function () {
  'use strict';

  /* ======================================================================
     Countdown
     ====================================================================== */

  class FusionCountdown extends HTMLElement {
    connectedCallback() {
      this.active = this.querySelector('[data-countdown-active]');
      this.expired = this.querySelector('[data-countdown-expired]');
      this.summary = this.querySelector('[data-countdown-summary]');

      this.units = {
        days: this.querySelector('[data-countdown-unit="days"]'),
        hours: this.querySelector('[data-countdown-unit="hours"]'),
        minutes: this.querySelector('[data-countdown-unit="minutes"]'),
        seconds: this.querySelector('[data-countdown-unit="seconds"]'),
      };

      this.endTime = this.parseEnd(this.dataset.end);

      // An unparseable date must not leave a frozen or negative timer on screen
      if (this.endTime === null) {
        this.showExpired();
        return;
      }

      this.tick();
      this.timer = window.setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    /**
     * Accepts "YYYY-MM-DD HH:MM" (the documented merchant format) as well as
     * "YYYY-MM-DD" and full ISO strings. The space form is normalised to a "T"
     * because Safari rejects the space variant.
     */
    parseEnd(value) {
      if (!value) return null;

      const normalised = value.trim().replace(' ', 'T');
      const parsed = Date.parse(normalised);
      if (!Number.isNaN(parsed)) return parsed;

      const fallback = Date.parse(value);
      return Number.isNaN(fallback) ? null : fallback;
    }

    tick() {
      const remaining = this.endTime - Date.now();

      if (remaining <= 0) {
        window.clearInterval(this.timer);
        this.showExpired();
        return;
      }

      const totalSeconds = Math.floor(remaining / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      this.set('days', days);
      this.set('hours', hours);
      this.set('minutes', minutes);
      this.set('seconds', seconds);

      if (this.summary) {
        this.summary.textContent = `${days}d ${hours}h ${minutes}m ${seconds}s remaining`;
      }
    }

    set(unit, value) {
      const el = this.units[unit];
      if (!el) return;
      // Never render a negative or unpadded value
      el.textContent = String(Math.max(0, value)).padStart(2, '0');
    }

    showExpired() {
      // 'hide' removes the whole section; anything else swaps in the message
      if (this.dataset.expiry === 'hide') {
        const section = this.closest('.shopify-section') || this;
        section.hidden = true;
        return;
      }

      this.active?.setAttribute('hidden', '');
      this.expired?.removeAttribute('hidden');
    }
  }

  if (!customElements.get('fusion-countdown')) {
    customElements.define('fusion-countdown', FusionCountdown);
  }

  /* ======================================================================
     Video loader
     ====================================================================== */

  /**
   * Replaces a poster image with the real embed only on interaction. Embedding
   * a YouTube or Vimeo iframe on load costs hundreds of kilobytes and several
   * third-party requests before the visitor has asked for the video.
   */
  class FusionVideoLoader extends HTMLElement {
    connectedCallback() {
      this.target = this.querySelector('[data-video-target]');
      const trigger = this.querySelector('[data-video-play]') || this.target;
      trigger?.addEventListener('click', () => this.load());

      // Popup mode: lazy-load into a <dialog> instead of replacing inline.
      // See sections/video.liquid's `is_popup` branch.
      this.popupTrigger = this.querySelector('[data-video-popup-trigger]');
      this.popupTrigger?.addEventListener('click', () => this.loadIntoDialog());
    }

    load() {
      const url = this.dataset.embedUrl;
      if (!url || !this.target) return;

      const iframe = document.createElement('iframe');
      iframe.src = url;
      iframe.className = 'video-section__iframe';
      iframe.title = this.dataset.title || '';
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      iframe.allowFullscreen = true;
      iframe.loading = 'lazy';

      this.target.replaceWith(iframe);
      iframe.focus();
    }

    loadIntoDialog() {
      const dialog = document.getElementById(this.popupTrigger.dataset.dialogTarget);
      const body = dialog?.querySelector('[data-video-popup-body]');
      if (!dialog || !body) return;

      // Already loaded from a previous open — just reopen, do not re-fetch.
      if (body.childElementCount === 0) {
        const embedUrl = this.popupTrigger.dataset.embedUrl;

        if (embedUrl) {
          const iframe = document.createElement('iframe');
          iframe.src = embedUrl;
          iframe.className = 'video-section__iframe';
          iframe.title = this.popupTrigger.dataset.title || '';
          iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
          iframe.allowFullscreen = true;
          body.appendChild(iframe);
        } else {
          const source = this.querySelector('[data-video-popup-source]');
          if (source) body.appendChild(source.content.cloneNode(true));
        }

        // Closing (Esc, backdrop click, or the close button) fires the
        // dialog's native `close` event — tearing the content down here
        // stops playback and frees decoded media, and clears the way for a
        // fresh load next time it opens.
        dialog.addEventListener('close', () => { body.innerHTML = ''; }, { once: true });
      }

      dialog.showModal();
    }
  }

  if (!customElements.get('fusion-video-loader')) {
    customElements.define('fusion-video-loader', FusionVideoLoader);
  }

  /* ======================================================================
     Lightbox
     ====================================================================== */

  class FusionLightbox extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-lightbox-dialog]');
      this.image = this.querySelector('[data-lightbox-image]');
      this.caption = this.querySelector('[data-lightbox-caption]');

      this.triggers = Array.from(this.querySelectorAll('[data-lightbox-open]'));
      this.index = 0;
      this.onKeydown = this.onKeydown.bind(this);

      this.triggers.forEach((trigger, i) => {
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          this.open(i);
        });
      });

      this.querySelectorAll('[data-lightbox-close]').forEach((el) => {
        el.addEventListener('click', () => this.close());
      });

      this.querySelector('[data-lightbox-prev]')?.addEventListener('click', () => {
        this.show(this.index - 1);
      });

      this.querySelector('[data-lightbox-next]')?.addEventListener('click', () => {
        this.show(this.index + 1);
      });
    }

    open(index) {
      if (!this.dialog) return;

      this.show(index);
      this.dialog.removeAttribute('hidden');
      this.classList.add('is-open');

      window.Fusion.lockScroll();
      this.releaseFocus = window.Fusion.trapFocus(
        this.dialog,
        this.querySelector('[data-lightbox-close]')
      );
      document.addEventListener('keydown', this.onKeydown);
    }

    show(index) {
      const total = this.triggers.length;
      if (total === 0) return;

      this.index = ((index % total) + total) % total;
      const trigger = this.triggers[this.index];

      if (this.image) {
        this.image.src = trigger.dataset.lightboxSrc || '';
        this.image.alt = trigger.dataset.lightboxAlt || '';
      }

      if (this.caption) {
        const text = trigger.dataset.lightboxCaption || '';
        this.caption.textContent = text;
        this.caption.hidden = text.length === 0;
      }
    }

    close() {
      this.dialog?.setAttribute('hidden', '');
      this.classList.remove('is-open');

      window.Fusion.unlockScroll();
      this.releaseFocus?.();
      this.releaseFocus = null;
      document.removeEventListener('keydown', this.onKeydown);

      // Free the decoded image
      this.image?.removeAttribute('src');
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.show(this.index - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.show(this.index + 1);
      }
    }
  }

  if (!customElements.get('fusion-lightbox')) {
    customElements.define('fusion-lightbox', FusionLightbox);
  }

  /* ======================================================================
     Testimonial carousel
     ====================================================================== */

  /**
   * A scroll-snap carousel. Position is tracked via a committed target index
   * rather than re-derived from live scrollLeft on every navigation, so
   * rapid or repeated arrow clicks during an in-flight smooth-scroll never
   * read a mid-animation scroll position. Deriving the index live caused a
   * real bug here: a quick second click could sample scrollLeft partway
   * through the first click's smooth scroll, compute the wrong "current"
   * slide, and wrap back to an earlier position — visually indistinguishable
   * from the carousel randomly repeating/looping instead of advancing.
   */
  /**
   * Generic scroll-snap carousel: a viewport whose children are the slides,
   * optional prev/next buttons, an optional screen-reader status region, and
   * optional clickable dots. Works with no JS at all (the viewport is a
   * native scroll container); this layers smooth prev/next/dot navigation,
   * keyboard arrows, autoplay and live-region announcements on top.
   *
   * Shared by every carousel in the theme — register a new custom element
   * name as `class extends FusionCarousel {}` (see fusion-collection-carousel
   * below) rather than duplicating this logic.
   */
  class FusionCarousel extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-carousel-viewport]');
      if (!this.viewport) return;

      this.status = this.querySelector('[data-carousel-status]');
      this.prev = this.querySelector('[data-carousel-prev]');
      this.next = this.querySelector('[data-carousel-next]');
      this.dotsContainer = this.querySelector('[data-carousel-dots]');
      this.dots = this.dotsContainer ? Array.from(this.dotsContainer.children) : [];
      this.items = Array.from(this.viewport.children);

      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.interval = parseInt(this.dataset.autoplayInterval, 10) || 6000;

      // Source of truth for button/keyboard/autoplay navigation. Never read
      // back from a getter derived off live scrollLeft for this — see class
      // doc comment above for why that caused the "repeats/loops" bug.
      this.targetIndex = this.current;

      this.prev?.addEventListener('click', () => this.scrollBySlides(-1));
      this.next?.addEventListener('click', () => this.scrollBySlides(1));

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.scrollToSlide(index));
      });

      this.viewport.addEventListener(
        'scroll',
        window.Fusion.throttle(() => this.sync(), 120),
        { passive: true }
      );

      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.scrollBySlides(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.scrollBySlides(1);
        }
      });

      this.sync();

      if (this.autoplayEnabled && this.items.length > 1 && window.Fusion.motionAllowed()) {
        this.start();
        this.addEventListener('mouseenter', () => this.stop());
        this.addEventListener('mouseleave', () => this.start());
        this.addEventListener('focusin', () => this.stop());
        this.addEventListener('focusout', () => this.start());
        document.addEventListener('visibilitychange', () => {
          document.hidden ? this.stop() : this.start();
        });
      }
    }

    disconnectedCallback() {
      this.stop();
    }

    /**
     * Only for reading the *actual* on-screen position (initial mount and
     * reconciling after the visitor drags/swipes the track by hand).
     * Button/keyboard/autoplay navigation must use `targetIndex` instead —
     * see the class doc comment.
     */
    get current() {
      if (this.items.length === 0) return 0;
      const width = this.items[0].offsetWidth || 1;
      return Math.round(this.viewport.scrollLeft / width);
    }

    scrollBySlides(direction) {
      const width = this.items[0]?.offsetWidth || 0;
      if (width === 0) return;

      const total = this.items.length;
      let target = this.targetIndex + direction;

      // Wrap around at both ends
      if (target < 0) target = total - 1;
      if (target > total - 1) target = 0;

      this.goTo(target, width);
    }

    scrollToSlide(index) {
      const width = this.items[0]?.offsetWidth || 0;
      if (width === 0) return;

      this.goTo(index, width);
    }

    goTo(index, width) {
      this.targetIndex = index;

      this.viewport.scrollTo({
        left: index * width,
        behavior: window.Fusion.motionAllowed() ? 'smooth' : 'auto',
      });

      // Update dots/status immediately rather than waiting for the throttled
      // scroll-driven sync() — keeps a fast double-click from visually
      // lagging a full slide behind the commanded position.
      this.render(index);
    }

    sync() {
      const index = this.current;
      // A manual drag/swipe is the one case where the on-screen position can
      // legitimately differ from targetIndex — reconcile it here so the next
      // button/keyboard press advances from where the visitor actually left it.
      this.targetIndex = index;
      this.render(index);
    }

    render(index) {
      if (this.status) {
        const template = this.dataset.statusTemplate || '';
        this.status.textContent = template
          ? template.replace('__index__', index + 1)
          : `${index + 1} / ${this.items.length}`;
      }

      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    }

    start() {
      this.stop();
      this.timer = window.setInterval(() => this.scrollBySlides(1), this.interval);
    }

    stop() {
      window.clearInterval(this.timer);
    }
  }

  if (!customElements.get('fusion-testimonial-carousel')) {
    customElements.define('fusion-testimonial-carousel', FusionCarousel);
  }

  if (!customElements.get('fusion-collection-carousel')) {
    customElements.define('fusion-collection-carousel', class extends FusionCarousel {});
  }

  if (!customElements.get('fusion-product-carousel')) {
    customElements.define('fusion-product-carousel', class extends FusionCarousel {});
  }
})();
